import React, { useEffect, useState, useCallback } from "react";
import { X, Share, Download, Plus } from "lucide-react";

/**
 * Cross-platform PWA install prompt.
 *
 * Chrome/Edge/Android: listens for `beforeinstallprompt`, shows a native
 * install dialog when the user clicks the button.
 *
 * iOS Safari: no programmatic install API exists, so we show a modal with
 * illustrated Share → "Add to Home Screen" steps.
 *
 * Hidden entirely when the app is already running as an installed PWA
 * (display-mode: standalone) or when the user has dismissed the prompt.
 */

const DISMISS_KEY = "myedge_pwa_install_dismissed";

function detectPlatform() {
  if (typeof window === "undefined") return "unknown";
  const ua = window.navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  return { isIOS, isSafari, isAndroid, isStandalone };
}

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState({ isStandalone: false });
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[InstallPWA] localStorage read blocked:", err?.message);
      }
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    const onInstalled = () => {
      setDeferredPrompt(null);
      setPlatform((p) => ({ ...p, isStandalone: true }));
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return;
    }
    if (platform.isIOS) {
      setShowIOSModal(true);
    }
  }, [deferredPrompt, platform.isIOS]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[InstallPWA] localStorage write blocked:", err?.message);
      }
    }
    setDismissed(true);
  }, []);

  // Hidden cases: already installed, dismissed, or no install path available.
  // Debug override: append `?pwa=1` to force show for QA/screenshots.
  const forceShow =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("pwa") === "1";
  if (!forceShow) {
    if (platform.isStandalone) return null;
    if (dismissed) return null;
    if (!deferredPrompt && !platform.isIOS) return null;
  }

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-4 right-4 sm:right-auto sm:left-auto sm:bottom-6 sm:right-6 z-50 max-w-sm surface rounded-sm p-4 border border-blue-800/60 shadow-lg"
      >
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center text-slate-500 hover:text-slate-200"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-sm bg-blue-950/60 border border-blue-800 flex items-center justify-center shrink-0">
            <Download size={18} className="text-blue-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-100 font-medium">
              Install MyEdge Trader
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
              Add to your home screen for one-tap access, full-screen UI, and
              faster load times.
            </div>
            <button
              data-testid="pwa-install-btn"
              onClick={handleInstall}
              className="mt-3 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[10px] uppercase tracking-[0.15em] rounded-sm"
            >
              {platform.isIOS ? "Show me how" : "Install"}
            </button>
          </div>
        </div>
      </div>

      {showIOSModal && (
        <div
          data-testid="pwa-ios-modal"
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSModal(false)}
        >
          <div
            className="surface rounded-sm p-6 max-w-md w-full border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium text-slate-100">
                Install on iPhone / iPad
              </div>
              <button
                data-testid="pwa-ios-close"
                onClick={() => setShowIOSModal(false)}
                className="h-7 w-7 flex items-center justify-center text-slate-400 hover:text-slate-100"
              >
                <X size={16} />
              </button>
            </div>
            <ol className="space-y-4 text-sm text-slate-300">
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 shrink-0 rounded-full bg-blue-950 border border-blue-800 text-blue-300 flex items-center justify-center text-xs font-mono">
                  1
                </span>
                <div>
                  Open this page in <span className="text-blue-300">Safari</span>{" "}
                  (not Chrome or in-app browsers).
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 shrink-0 rounded-full bg-blue-950 border border-blue-800 text-blue-300 flex items-center justify-center text-xs font-mono">
                  2
                </span>
                <div className="flex-1">
                  Tap the{" "}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-slate-800 border border-slate-700 text-slate-100 align-middle">
                    <Share size={12} />
                    Share
                  </span>{" "}
                  button in the bottom toolbar.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 shrink-0 rounded-full bg-blue-950 border border-blue-800 text-blue-300 flex items-center justify-center text-xs font-mono">
                  3
                </span>
                <div className="flex-1">
                  Scroll and tap{" "}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-slate-800 border border-slate-700 text-slate-100 align-middle">
                    <Plus size={12} />
                    Add to Home Screen
                  </span>
                  , then tap <span className="text-blue-300">Add</span>.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 shrink-0 rounded-full bg-emerald-950 border border-emerald-700 text-emerald-300 flex items-center justify-center text-xs font-mono">
                  ✓
                </span>
                <div>
                  The MyEdge icon appears on your home screen — tap to launch
                  full-screen.
                </div>
              </li>
            </ol>
            <button
              data-testid="pwa-ios-got-it"
              onClick={() => {
                setShowIOSModal(false);
                dismiss();
              }}
              className="mt-6 w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs uppercase tracking-[0.15em] rounded-sm"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
